<template>
  <div class="bin-print">
    <div class="print-toolbar no-print">
      <button type="button" class="btn" @click="goBack">← Inventory</button>
      <button type="button" class="btn primary" @click="doPrint">Print</button>
      <label class="hide-empty">
        <input v-model="hideEmpty" type="checkbox">
        Skip empty boxes
      </label>
      <span class="muted">{{ sheets.length }} sheet{{ sheets.length === 1 ? '' : 's' }} · initials columns {{ packYears.join(' · ') }}</span>
    </div>

    <p v-if="loading" class="muted no-print">Loading inventory…</p>

    <section
      v-for="sheet in sheets"
      :key="sheet.id"
      class="bin-sheet"
    >
      <header class="bin-head">
        <p class="brand">IABF festival packing</p>
        <h1>{{ sheet.title }}</h1>
        <p class="sub">Write initials when this bin is packed. One column per year.</p>
      </header>

      <table class="pack-table">
        <thead>
          <tr>
            <th class="qty">Qty</th>
            <th class="item">Item</th>
            <th class="notes">Notes</th>
            <th
              v-for="y in packYears"
              :key="y"
              class="year"
            >{{ y }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="row in sheet.rows" :key="row.key">
            <td class="qty">{{ row.qty }}</td>
            <td class="item">{{ row.name }}</td>
            <td class="notes">{{ row.notes }}</td>
            <td
              v-for="y in packYears"
              :key="y"
              class="year"
            >
              <span class="initials-box"></span>
            </td>
          </tr>
          <tr v-for="n in extraRows" :key="'blank-' + sheet.id + '-' + n" class="blank-row">
            <td class="qty"></td>
            <td class="item"></td>
            <td class="notes"></td>
            <td
              v-for="y in packYears"
              :key="y"
              class="year"
            >
              <span class="initials-box"></span>
            </td>
          </tr>
        </tbody>
      </table>
    </section>
  </div>
</template>

<script>
import { IABF_DANCER_YEAR } from '@/config/iabf-event'
import {
  closetBorrowNote,
  closetCategoryLabel
} from '@/config/iabf-costume-closet'
import {
  INVENTORY_BOXES,
  CLOSET_BOX_ID,
  boxLabel,
  becomingItemName,
  isCostumeCheckedIn
} from '@/config/iabf-inventory'

const PACK_YEARS = [2026, 2027, 2028, 2029]
const EXTRA_BLANK_ROWS = 4

export default {
  name: 'FestivalInventoryPrint',
  data () {
    return {
      loading: true,
      hideEmpty: false,
      packYears: PACK_YEARS,
      extraRows: EXTRA_BLANK_ROWS
    }
  },
  computed: {
    items () {
      return this.$store.getters.festivalInventory || []
    },
    tutus () {
      return this.$store.getters.costumeInventory || []
    },
    supplies () {
      return this.items.filter(i => i.category === 'supply')
    },
    becomingItems () {
      return this.items.filter(i => i.category === 'becoming')
    },
    closetItems () {
      return this.$store.getters.costumeCloset || []
    },
    closetLoans () {
      return this.$store.getters.closetLoans || []
    },
    packYear () {
      return Number(this.$store.getters.adminFestivalYear) || IABF_DANCER_YEAR
    },
    sheets () {
      const boxes = INVENTORY_BOXES.map(box => {
        const rows = this.rowsForBox(box.id)
        return {
          id: box.id,
          title: boxLabel(box.id),
          rows
        }
      })
      if (this.hideEmpty) {
        return boxes.filter(b => b.rows.length)
      }
      return boxes
    }
  },
  created () {
    this.$store.commit('setLayout', 'iabf-layout')
    Promise.all([
      this.$store.dispatch('loadFestivalInventory'),
      this.$store.dispatch('loadCostumeInventory'),
      this.$store.dispatch('loadCostumeCloset'),
      this.$store.dispatch('loadClosetLoans', this.packYear)
    ]).finally(() => {
      this.loading = false
    })
  },
  methods: {
    goBack () {
      this.$router.push('/iabf/admin/inventory')
    },
    doPrint () {
      window.print()
    },
    formatQty (qty) {
      if (qty === 0) {
        return '0'
      }
      if (qty == null || qty === '') {
        return ''
      }
      return String(qty)
    },
    rowsForBox (boxId) {
      const n = Number(boxId)
      const supplies = this.supplies
        .filter(i => Number(i.box) === n)
        .slice()
        .sort((a, b) => String(a.name || '').localeCompare(String(b.name || '')))
        .map(i => ({
          key: 's-' + i.id,
          name: i.name || '',
          notes: i.notes || '',
          qty: this.formatQty(i.qty)
        }))
      const tutus = this.tutus
        .filter(t => isCostumeCheckedIn(t) && Number(t.storageBox) === n)
        .map(t => ({
          key: 't-' + t.id,
          name: t.name + (t.color ? (' · ' + t.color) : ''),
          notes: 'Tutu',
          qty: '1'
        }))
      const becoming = this.becomingItems
        .filter(b => isCostumeCheckedIn(b) && Number(b.storageBox) === n)
        .map(b => ({
          key: 'b-' + b.id,
          name: becomingItemName(b.becomingKind, b.size),
          notes: 'Becoming',
          qty: this.formatQty(b.qty)
        }))
      let closet = []
      if (n === CLOSET_BOX_ID) {
        closet = this.closetItems
          .slice()
          .sort((a, b) => {
            const cat = closetCategoryLabel(a.category).localeCompare(closetCategoryLabel(b.category))
            if (cat !== 0) {
              return cat
            }
            return String(a.name || '').localeCompare(String(b.name || ''))
          })
          .map(item => ({
            key: 'c-' + item.id,
            name: item.name || '',
            notes: closetBorrowNote(item, this.closetLoans, this.packYear),
            qty: this.formatQty(item.qty)
          }))
      }
      return supplies.concat(tutus).concat(becoming).concat(closet)
    }
  }
}
</script>

<style scoped>
.bin-print {
  padding: 16px 20px 48px;
  color: #1c1b1f;
  background: #fff;
}
.print-toolbar {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 20px;
}
.btn {
  min-height: 36px;
  padding: 6px 12px;
  border: 1.5px solid #c8c4ce;
  border-radius: 8px;
  background: #fff;
  font: inherit;
  cursor: pointer;
}
.btn.primary {
  background: #4a5d3a;
  border-color: #4a5d3a;
  color: #fff;
  font-weight: 700;
}
.hide-empty {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
}
.muted {
  color: #5c5a63;
  font-size: 13px;
}
.bin-sheet {
  max-width: 8.5in;
  margin: 0 auto 28px;
  page-break-after: always;
  page-break-inside: avoid;
}
.bin-sheet:last-child {
  page-break-after: auto;
}
.bin-head {
  margin-bottom: 10px;
  border-bottom: 2px solid #1c1b1f;
  padding-bottom: 8px;
}
.brand {
  margin: 0;
  font-size: 11px;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: #5c5a63;
}
.bin-head h1 {
  margin: 2px 0 4px;
  font-family: Georgia, 'Times New Roman', serif;
  font-size: 22px;
  font-weight: 400;
}
.sub {
  margin: 0;
  font-size: 12px;
  color: #5c5a63;
}
.pack-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;
}
.pack-table th,
.pack-table td {
  border: 1px solid #1c1b1f;
  padding: 6px 8px;
  vertical-align: middle;
  text-align: left;
}
.pack-table th {
  background: #f3f1ec;
  font-size: 11px;
  font-weight: 700;
}
.qty {
  width: 48px;
  text-align: center;
}
.notes {
  width: 28%;
  color: #3d3b42;
}
.year {
  width: 52px;
  text-align: center;
}
.initials-box {
  display: inline-block;
  width: 2.4em;
  height: 1.35em;
  border-bottom: 1px solid #1c1b1f;
}
.blank-row td {
  height: 28px;
}

@media print {
  .no-print {
    display: none !important;
  }
  .bin-print {
    padding: 0;
  }
  .bin-sheet {
    margin: 0;
    max-width: none;
  }
  .pack-table {
    font-size: 11px;
  }
}
</style>
